import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { notification } from "antd";
import {
  ImprovementsService,
  EligibleModel,
} from "../../../services/ImprovementsService";
import { Card, CardContent } from "../../ui/card";
import PaginatedTable, { PaginatedTableColumn } from "../../ui/paginated-table";
import { TrendingUpIcon } from "lucide-react";

const INTERNAL_COMPANY_IDS = [2, 51];

const Improvements: React.FC = () => {
  const { apiKey, companyId } = usePermissions();
  const navigate = useNavigate();
  const [models, setModels] = useState<EligibleModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const isInternal = useMemo(
    () => !!companyId && INTERNAL_COMPANY_IDS.includes(companyId),
    [companyId],
  );

  const fetchModels = useCallback(() => {
    if (!isInternal) return;
    ImprovementsService.getEligibleModels(setIsLoading, apiKey).subscribe(
      (response: EligibleModel[]) => {
        setModels(response || []);
      },
      () => {
        notification.error({
          message: "Error",
          description: "Failed to load eligible models",
        });
      }
    );
  }, [apiKey, isInternal]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const columns: PaginatedTableColumn<EligibleModel>[] = [
    {
      key: "company_name",
      header: "Company",
      width: "20%",
      render: (item) => (
        <span className="font-medium text-foreground">
          {item.company_name || `ID: ${item.company_id}`}
        </span>
      ),
    },
    {
      key: "model",
      header: "Model",
      width: "30%",
      render: (item) => (
        <span
          className="font-mono text-xs text-primary hover:underline cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/forge/finetuning/${item.finetune_run_id}`);
          }}
        >
          {item.model}
        </span>
      ),
    },
    {
      key: "base_model",
      header: "Base Model",
      width: "25%",
      render: (item) => (
        <span className="text-xs text-muted-foreground">
          {item.base_model}
        </span>
      ),
    },
    {
      key: "profile_status",
      header: "Profile",
      width: "10%",
      render: (item) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          item.profile_status === "COMPLETED"
            ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
            : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"
        }`}>
          {item.profile_status}
        </span>
      ),
    },
  ];

  if (!isInternal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">
          This page is only available to internal Maitai users.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUpIcon className="h-6 w-6 text-muted-foreground" />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-semibold tracking-tight">
                      Improvements
                    </h1>
                    <span className="text-[10px] font-gc-med uppercase tracking-wide px-1.5 py-0.5 rounded bg-maitai-green/15 text-maitai-green">
                      Beta
                    </span>
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Eligible models with completed profiles. Click a model to
                    view drift analysis history and trigger ad-hoc runs.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <PaginatedTable
          data={models}
          columns={columns}
          loading={isLoading}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          totalItems={models.length}
          emptyTitle="No eligible models"
          emptyDescription="Models need to be finetuned, enabled, and have a completed model profile."
          onRowClick={(item) =>
            navigate(`/admin/improvements/${item.id}`, {
              state: { model: item },
            })
          }
          rowClassName="cursor-pointer"
        />
      </div>
    </div>
  );
};

export default Improvements;
